import { FC, ReactNode } from "react"
import styles from "./SupplierAccountCard.module.scss"
import BtButton from "../../bt-button/bt-button"
import { isEmpty } from "lodash"
import TooltipContainer from "../../../components/shared/TooltipContainer/TooltipContainer"
import { logMessage } from "../../../services/service-logs";
import { SlackChannels } from "../../../util/appEnum"
import { useTranslation, Trans } from "react-i18next"
import { logEvent } from "../../../services/eventsService"
import appState from "../../../state/AppStateContainer"

export type SupplierStatus = {
  organizationNumber?: string,
  numOfUnansweredQuotations?: number,
  numOfQuotationsLast12Months?: number,
  numOfOrganizationsSupplierIsMissing?: number,
  notPartOfSalesOrg?: boolean,
  noSupplierProfile?: boolean,
  orgSlug?: string,
  adminUsers?: {
    name: string,
    email: string,
  }[],
  /** Buyer that sent the bid request the supplier is currently looking at. */
  customerOrganizationNumber?: string,
  customerName?: string,
  /** The logged in supplier contact. */
  contact?: {
    name?: string,
    email?: string,
    title?: string,
    phoneNumber?: string,
  },
}

type Props = {
  supplierStatus?: SupplierStatus,
}

/** Identifies both the click event and every wizard event in the signup that follows. */
const SIGNUP_SOURCE = 'quotation';

/**
 * Query string that prefills the supplier signup wizard with what we already
 * know about the supplier and the contact clicking the banner.
 */
const signupPrefillParams = (
  supplierStatus: SupplierStatus | undefined,
  contactName?: string,
  contactEmail?: string,
): string => {
  const params = new URLSearchParams({ source: SIGNUP_SOURCE, addAsContact: 'true' });
  const prefill: Record<string, string | undefined> = {
    organizationNumber: supplierStatus?.organizationNumber,
    name: contactName,
    email: contactEmail,
    title: supplierStatus?.contact?.title,
    phoneNumber: supplierStatus?.contact?.phoneNumber,
  };
  Object.entries(prefill).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  return params.toString();
}

export const SupplierAccountCard: FC<Props> = ({ supplierStatus }) => {
  const [t] = useTranslation('quotation', { keyPrefix: 'supplierAccount' });

  if (isEmpty(supplierStatus)) return null;

  let text: ReactNode = null;
  let buttonText = '';

  if (supplierStatus?.numOfUnansweredQuotations) {
    text = <span><Trans t={t} i18nKey="info.numOfUnansweredQuotations" values={{count: supplierStatus?.numOfUnansweredQuotations }} components={{b: <b />}} /></span>;
    buttonText = t('button.createAccount', 'Skapa konto');
  } else if (supplierStatus?.numOfQuotationsLast12Months) {
    text = <span><Trans t={t} i18nKey="info.numOfQuotationsLast12Months" values={{count: supplierStatus?.numOfQuotationsLast12Months }} components={{b: <b />}} /></span>;
    buttonText = t('button.createAccount', 'Skapa konto');
  } else if (supplierStatus?.numOfOrganizationsSupplierIsMissing) {
    text = <span><Trans t={t} i18nKey="info.numOfOrganizationsSupplierIsMissing" values={{count: supplierStatus?.numOfOrganizationsSupplierIsMissing }} components={{b: <b />}} /></span>;
    buttonText = t('button.createAccount', 'Skapa konto');
  } else if (supplierStatus?.notPartOfSalesOrg) {
    text = <div>
      <span><Trans
        t={t}
        i18nKey="info.notPartOfSalesOrg"
        components={{administrators: <TooltipContainer
          renderReferenceComponent={(className, ref) => (
            <span className={className}
              ref={ref}
              style={{ borderBottom: '1px dotted var(--light-blue-800)', cursor: 'default' }}
            >{t('info.notPartOfSalesOrgAdministrators', 'administratörerna')}</span>
          )}
        >
          {supplierStatus?.adminUsers && supplierStatus?.adminUsers?.length > 0 && (
            <ul>
              {supplierStatus.adminUsers.map((adminUser) => <li>{adminUser.name} ({adminUser.email})</li>)}
            </ul>
          )}
        </TooltipContainer>}}
      /></span>
    </div>;
  } else if (supplierStatus?.noSupplierProfile) {
    text = <span>{t('info.noSupplierProfile', 'Du har ett leverantörskonto, men leverantörspresentationen är inte ifylld. Ta kontroll över hur ert företag presenteras för inköpare genom att fylla i presentationen.')}</span>;
    buttonText = t('button.addPresentation', 'Fyll i presentation');
  }

  const onClick = async () => {
    const notRedirect =
      window.location.host === 'localhost:3000' ||
      window.location.host === 'test.accurator.se' ||
      window.location.host === 'app.accurator.se';
    const href = notRedirect ? window.location.href.split('/').slice(0, 3).join('/') : `https://app.accurator.se`;
    if (buttonText === t('button.createAccount', 'Skapa konto')) {
      const contactName = supplierStatus?.contact?.name || appState.getUserName();
      const contactEmail = supplierStatus?.contact?.email || appState.getUserEmail();

      await logEvent({
        event: 'CLICKED_CREATE_SUPPLIER_ACCOUNT',
        userIdentifier: contactEmail,
        metadata: {
          source: SIGNUP_SOURCE,
          supplierOrganizationNumber: supplierStatus?.organizationNumber,
          supplierContactName: contactName,
          supplierContactEmail: contactEmail,
          customerOrganizationNumber: supplierStatus?.customerOrganizationNumber,
          customerName: supplierStatus?.customerName,
        },
        options: {
          unique: true,
        }
      });
      // The backend appends the supplier contact, their org number and the
      // customer name to the Slack message — only the customer org number and
      // the source need to be spelled out here.
      await logMessage(
        `New supplier account creation button click from the quotation banner (customer org number: ${supplierStatus?.customerOrganizationNumber ?? '-'})`,
        SlackChannels.SUPPLIERS
      );
      window.location.href = `${href}/supplier-sign-up?${signupPrefillParams(supplierStatus, contactName, contactEmail)}`;
    } else if (buttonText === t('button.addPresentation', 'Fyll i presentation')) {
      window.location.href = `${href}/${supplierStatus?.orgSlug}/supplier-presentation`;
    }
  }

  return (
    <div className={styles.card}>
      <div className={styles.container}>
        <p>{t('heading', 'Leverantörskonto')}</p>
        <div className={styles.content}>
          {text}
        </div>
      </div>
      {buttonText && <BtButton color="white" onClick={onClick}>{buttonText}</BtButton>}
    </div>
  )
}
